import React, { useEffect, useState, useMemo } from 'react';
import { usersApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { getRoleConfig } from '../../lib/utils';
import { Search, Users, Edit, Trash2, UserPlus, Check, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

// Password validation helper
const validatePassword = (password) => {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
};

const isPasswordValid = (password) => {
  const checks = validatePassword(password);
  return checks.minLength && checks.hasUppercase && checks.hasNumber && checks.hasSpecial;
};

// Password Requirements Component
const PasswordRequirements = ({ password }) => {
  const checks = validatePassword(password || '');
  
  const requirements = [
    { key: 'minLength', label: 'At least 8 characters', met: checks.minLength },
    { key: 'hasUppercase', label: 'One uppercase letter (A-Z)', met: checks.hasUppercase },
    { key: 'hasNumber', label: 'One number (0-9)', met: checks.hasNumber },
    { key: 'hasSpecial', label: 'One special character (!@#$%^&*)', met: checks.hasSpecial },
  ];

  return (
    <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
      <p className="text-xs font-medium text-gray-600 mb-2">Password Requirements:</p>
      <div className="space-y-1">
        {requirements.map((req) => (
          <div key={req.key} className="flex items-center gap-2 text-xs">
            {req.met ? (
              <Check className="w-3 h-3 text-green-600" />
            ) : (
              <X className="w-3 h-3 text-red-500" />
            )}
            <span className={req.met ? 'text-green-700' : 'text-gray-500'}>
              {req.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DEPARTMENTS = [
  'Administrative Assistant',
  'Business Development',
  'Contracts',
  'Digital OPS & Traffic',
  'Finance',
  'Human Resource',
  'Information Technology',
  'Internal Audit',
  'Investor Relations',
  'IR & Governance',
  'Kitchen',
  'Maintenance',
  'Management',
  'Marketing',
  'Project Management',
  'Purchasing',
  'Reception',
  'Sales',
];

const BRANCHES = [
  'Boulevard',
  'Head Office',
  'Jeddah',
  'Madeena',
  'Murooj Office',
  'Warehouse',
];

const initialNewUser = {
  email: '',
  name: '',
  password: '',
  role: 'employee',
  department: '',
  branch: 'Head Office',
  managerId: null,
  pdfToolsAccess: false,
};

export const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [newUser, setNewUser] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersData, managersData] = await Promise.all([
        usersApi.getAll(),
        usersApi.getManagers(),
      ]);
      setUsers(usersData);
      setManagers(managersData);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error) => {
    const detail = error.response?.data?.detail;
    if (!detail) return 'An error occurred';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // Pydantic validation errors
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    if (typeof detail === 'object') {
      return detail.msg || detail.message || JSON.stringify(detail);
    }
    return 'An error occurred';
  };

  const handleCreate = async () => {
    if (!newUser) return;
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate password strength
    if (!isPasswordValid(newUser.password)) {
      toast.error('Password does not meet the requirements');
      return;
    }
    
    setSaving(true);
    try {
      const created = await usersApi.create({
        email: newUser.email,
        name: newUser.name,
        password: newUser.password,
        role: newUser.role,
        department: newUser.department || null,
        branch: newUser.branch || null,
        managerId: newUser.managerId || null,
        pdfToolsAccess: newUser.pdfToolsAccess || false,
      });
      setUsers([...users, created]);
      // Refresh managers list if new user is a manager
      if (newUser.role === 'manager' || newUser.role === 'admin') {
        const managersData = await usersApi.getManagers();
        setManagers(managersData);
      }
      toast.success('User created successfully');
      setNewUser(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editUser) return;
    
    // Validate new password if provided
    if (editUser.newPassword && !isPasswordValid(editUser.newPassword)) {
      toast.error('New password does not meet the requirements');
      return;
    }
    
    setSaving(true);
    try {
      const updatePayload = {
        name: editUser.name,
        department: editUser.department,
        branch: editUser.branch,
        role: editUser.role,
        managerId: editUser.managerId || null,
        pdfToolsAccess: editUser.pdfToolsAccess || false,
      };
      // Only include password if it was changed
      if (editUser.newPassword) {
        updatePayload.password = editUser.newPassword;
      }
      const updated = await usersApi.update(editUser.id, updatePayload);
      setUsers(users.map((u) => (u.id === updated.id ? updated : u)));
      // Refresh managers list if role changed
      const managersData = await usersApi.getManagers();
      setManagers(managersData);
      toast.success(editUser.newPassword ? 'User updated with new password' : 'User updated successfully');
      setEditUser(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      await usersApi.delete(deleteUser.id);
      setUsers(users.filter((u) => u.id !== deleteUser.id));
      toast.success('User deleted');
      setDeleteUser(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.department?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">User Management</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage users, roles, and employee-manager assignments
          </p>
        </div>
        <Button
          onClick={() => setNewUser({ ...initialNewUser })}
          className="bg-[#6a5da4] hover:bg-[#5a4d94]"
          data-testid="add-user-button"
        >
          <UserPlus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No users found</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {search ? 'Try adjusting your search' : 'No users in the system'}
              </p>
              <Button
                onClick={() => setNewUser({ ...initialNewUser })}
                className="bg-[#6a5da4] hover:bg-[#5a4d94]"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Add First User
              </Button>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Role</TableHead>
                      <TableHead className="font-semibold">Department</TableHead>
                      <TableHead className="font-semibold">Branch</TableHead>
                      <TableHead className="font-semibold">Manager</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => {
                      const roleConfig = getRoleConfig(user.role);
                      return (
                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleConfig.color}`}>
                              {roleConfig.label}
                            </span>
                          </TableCell>
                          <TableCell>{user.department || '-'}</TableCell>
                          <TableCell>{user.branch || '-'}</TableCell>
                          <TableCell>{user.managerName || '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditUser({ ...user })}
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteUser(user)}
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden p-4 space-y-3">
                {filteredUsers.map((user) => {
                  const roleConfig = getRoleConfig(user.role);
                  return (
                    <div key={user.id} className="bg-white border rounded-lg p-4 shadow-sm" data-testid={`user-card-${user.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold">{user.name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleConfig.color}`}>
                          {roleConfig.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Department</p>
                          <p>{user.department || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Branch</p>
                          <p>{user.branch || '-'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-muted-foreground">Manager</p>
                          <p>{user.managerName || '-'}</p>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 border-t pt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditUser({ ...user })}
                        >
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 border-red-200 hover:bg-red-50"
                          onClick={() => setDeleteUser(user)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={!!newUser} onOpenChange={(open) => !open && setNewUser(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account with role and department assignment
            </DialogDescription>
          </DialogHeader>

          {newUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-email">Email *</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="user@company.com"
                  data-testid="new-user-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-name">Full Name *</Label>
                <Input
                  id="new-name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="John Doe"
                  data-testid="new-user-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">Password *</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="new-user-password"
                  className={newUser.password && !isPasswordValid(newUser.password) ? 'border-amber-500' : ''}
                />
                <PasswordRequirements password={newUser.password} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-role">Role *</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({ ...newUser, role: value, managerId: null })}
                >
                  <SelectTrigger data-testid="new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="internal_audit">Internal Audit</SelectItem>
                    <SelectItem value="finance_head">Finance Head</SelectItem>
                    <SelectItem value="accounts">Accounts</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-department">Department</Label>
                  <Select
                    value={newUser.department || ''}
                    onValueChange={(value) => setNewUser({ ...newUser, department: value })}
                  >
                    <SelectTrigger data-testid="new-user-department">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-branch">Branch</Label>
                  <Select
                    value={newUser.branch || 'Head Office'}
                    onValueChange={(value) => setNewUser({ ...newUser, branch: value })}
                  >
                    <SelectTrigger data-testid="new-user-branch">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRANCHES.map((branch) => (
                        <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {newUser.role === 'employee' && (
                <div className="space-y-2">
                  <Label htmlFor="new-manager">Assigned Manager</Label>
                  <Select
                    value={newUser.managerId || 'none'}
                    onValueChange={(value) => setNewUser({ ...newUser, managerId: value === 'none' ? null : value })}
                  >
                    <SelectTrigger data-testid="new-user-manager">
                      <SelectValue placeholder="Select manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Manager</SelectItem>
                      {managers.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name} ({m.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* PDF Tools Access */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Checkbox
                  id="new-pdf-tools-access"
                  checked={newUser.pdfToolsAccess || false}
                  onCheckedChange={(checked) => setNewUser({ ...newUser, pdfToolsAccess: checked })}
                  className="data-[state=checked]:bg-[#6a5da4] data-[state=checked]:border-[#6a5da4]"
                  data-testid="new-user-pdf-tools-access"
                />
                <div className="flex-1">
                  <Label htmlFor="new-pdf-tools-access" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#6a5da4]" />
                    PDF Tools Access
                  </Label>
                  <p className="text-xs text-gray-500">Allow user to access the Add Logo to PDF tool</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setNewUser(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={saving || !newUser?.email || !newUser?.name || !newUser?.password || !isPasswordValid(newUser?.password || '')}
              className="bg-[#6a5da4] hover:bg-[#5a4d94]"
              data-testid="create-user-button"
            >
              {saving ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details, role assignments, or change password
            </DialogDescription>
          </DialogHeader>

          {editUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  value={editUser.email}
                  disabled
                  className="bg-gray-50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={editUser.name}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                  data-testid="edit-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={editUser.newPassword || ''}
                  onChange={(e) => setEditUser({ ...editUser, newPassword: e.target.value })}
                  placeholder="Leave blank to keep current password"
                  data-testid="edit-password"
                  className={editUser.newPassword && !isPasswordValid(editUser.newPassword) ? 'border-amber-500' : ''}
                />
                {editUser.newPassword && (
                  <PasswordRequirements password={editUser.newPassword} />
                )}
                {!editUser.newPassword && (
                  <p className="text-xs text-muted-foreground">
                    Only fill this if you want to change the password
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={editUser.role}
                  onValueChange={(value) => setEditUser({ ...editUser, role: value })}
                >
                  <SelectTrigger data-testid="edit-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="internal_audit">Internal Audit</SelectItem>
                    <SelectItem value="finance_head">Finance Head</SelectItem>
                    <SelectItem value="accounts">Accounts</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Select
                    value={editUser.department || ''}
                    onValueChange={(value) => setEditUser({ ...editUser, department: value })}
                  >
                    <SelectTrigger data-testid="edit-department">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="branch">Branch</Label>
                  <Select
                    value={editUser.branch || 'Head Office'}
                    onValueChange={(value) => setEditUser({ ...editUser, branch: value })}
                  >
                    <SelectTrigger data-testid="edit-branch">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRANCHES.map((branch) => (
                        <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {editUser.role === 'employee' && (
                <div className="space-y-2">
                  <Label htmlFor="manager">Assigned Manager</Label>
                  <Select
                    value={editUser.managerId || 'none'}
                    onValueChange={(value) => setEditUser({ ...editUser, managerId: value === 'none' ? null : value })}
                  >
                    <SelectTrigger data-testid="edit-manager">
                      <SelectValue placeholder="Select manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Manager</SelectItem>
                      {managers.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name} ({m.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* PDF Tools Access */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Checkbox
                  id="edit-pdf-tools-access"
                  checked={editUser.pdfToolsAccess || false}
                  onCheckedChange={(checked) => setEditUser({ ...editUser, pdfToolsAccess: checked })}
                  className="data-[state=checked]:bg-[#6a5da4] data-[state=checked]:border-[#6a5da4]"
                  data-testid="edit-user-pdf-tools-access"
                />
                <div className="flex-1">
                  <Label htmlFor="edit-pdf-tools-access" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#6a5da4]" />
                    PDF Tools Access
                  </Label>
                  <p className="text-xs text-gray-500">Allow user to access the Add Logo to PDF tool</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#6a5da4] hover:bg-[#5a4d94]"
              data-testid="save-user"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteUser} onOpenChange={(open) => !open && setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteUser?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagementPage;
